import { type FC, type ReactNode } from 'react';

// MUI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

interface Props {
  label: string;
  /** Tử số (số liệu của trường / được chọn). null → hiển thị "—". */
  numerator?: number | null;
  /** Mẫu số (tổng). Bỏ trống → chỉ hiện tử số, không hiện dạng x/y. */
  denominator?: number | null;
  /** Phần trăm hiển thị trong badge cam. Bỏ trống → tự tính từ tử/mẫu. */
  percent?: number | null;
  icon?: ReactNode;
}

const fmt = (n: number) => n.toLocaleString('vi-VN');

const StatCard: FC<Props> = ({ label, numerator, denominator, percent, icon }) => {
  const hasNum = numerator !== null && numerator !== undefined;
  const hasDen = denominator !== null && denominator !== undefined;

  const computedPercent =
    percent ?? (hasNum && hasDen && denominator ? Math.round((numerator! / denominator) * 100) : null);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        flex: 1,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 24px rgba(22, 28, 82, 0.05)',
      }}
    >
      {/* Cột trái: nhãn trên, số dưới */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5 }}>
          {icon}
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25 }}>
          {hasNum ? (
            <>
              <Typography sx={{ fontWeight: 800, fontSize: 36, lineHeight: 1, color: 'text.primary' }}>
                {fmt(numerator!)}
              </Typography>
              {hasDen && (
                <Typography sx={{ color: 'text.secondary', fontSize: 18 }}>
                  /{fmt(denominator!)}
                </Typography>
              )}
            </>
          ) : (
            // Không có tử số (vd không chọn khách) → chỉ hiện mẫu số làm số chính.
            <Typography sx={{ fontWeight: 800, fontSize: 36, lineHeight: 1, color: 'text.primary' }}>
              {hasDen ? fmt(denominator!) : '—'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Badge % cam, căn giữa theo chiều dọc bên phải */}
      {computedPercent !== null && (
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: 'primary.light',
            borderRadius: 1.5,
            px: 1.25,
            py: 0.25,
          }}
        >
          <Typography sx={{ color: 'primary.dark', fontWeight: 700, fontSize: 26, lineHeight: 1.2 }}>
            {computedPercent}%
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default StatCard;
